import { useState, useMemo } from 'react';
import { Plus, CreditCard, Landmark, Phone, FileCheck, Search, X } from 'lucide-react';
import { useRPCustomers, useRPCustomerCards, useRPCustomerBanks, type RPCustomer } from '@/hooks/use-receive-payments';
import { formatCurrency } from '@/lib/utils';
import { cn } from '@/lib/utils';
import { US_STATES } from '@/lib/constants';
import { toast } from '@/stores/toast-store';
import { useCreateSaleTransaction } from '@/hooks/use-sale-transactions';
import {
  useCreateRecurringSale,
  RECURRING_FREQUENCIES,
  RECURRING_FREQUENCY_LABELS,
  type RecurringFrequency,
} from '@/hooks/use-recurring-sales';
import { usePermissions } from '@/hooks/use-permissions';
import { validateDriversLicense, sanitizeDriversLicense, validatePhone } from '@/lib/validators';
import { PhoneInput } from '@/components/ui/phone-input';
import { DateInput } from '@/components/ui/date-input';
import { useLocationStore } from '@/stores/location-store';
import { resolveReceivePaymentMethods } from '@/lib/receive-payment-methods';
import { useLocationsWithExternalId } from '@/hooks/use-locations';

// ── Shared form utilities (matching registration-wizard patterns) ──

const inputBase = 'h-10 w-full rounded-lg border bg-white px-3 text-sm outline-none focus:ring-1';
const inputNormal = `${inputBase} border-input focus:border-primary focus:ring-primary`;
const inputError = `${inputBase} border-red-500 focus:border-red-500 focus:ring-red-500`;

type FieldErrors = Record<string, string>;

function FieldError({ message }: { message?: string }) {
  if (!message) return null;
  return (
    <p className="mt-1 text-xs text-red-500" role="alert">
      {message}
    </p>
  );
}

function fc(fieldErrors: FieldErrors, field: string): string {
  return fieldErrors[field] ? inputError : inputNormal;
}

// Generates a placeholder Driver's License value matching our format
// (1 uppercase letter + 7 digits). Used to prefill the ACH/prearranged DL
// field for customers who don't have a license — they can overwrite it.
function generatePlaceholderDriversLicense(): string {
  const letter = String.fromCharCode(65 + Math.floor(Math.random() * 26));
  const digits = Array.from({ length: 7 }, () => Math.floor(Math.random() * 10)).join('');
  return `${letter}${digits}`;
}

// ── Form State Types ──

interface NewSaleFormData {
  // Transaction type
  transactionType: string;
  // Sale details
  amount: string;
  notes: string;
  // Payment info
  customerId: string;
  paymentMethod: 'ach' | 'card';
  // Account info (ACH)
  accountType: string;
  accountCategory: string;
  routingNumber: string;
  accountNumber: string;
  authorizationType: string;
  // Account holder info (ACH)
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  streetAddress: string;
  city: string;
  state: string;
  zipCode: string;
  // Account holder ID (ACH)
  idType: string;
  dlState: string;
  dlNumber: string;
  // Card details (Credit / Debit Card)
  cardNumber: string;
  cardHolderName: string;
  expirationDate: string;
  cvv: string;
  cardZipCode: string;
  cardStreetAddress: string;
  cardAuthorizationType: string;
  cardEmail: string;
  // Authorization
  authorizationRequired: 'yes' | 'no';
  // Receipt
  receiptDelivery: string;
}

/** Shown on hover and on click of a payment-method tab the super admin has
 *  switched off for this location (Super Admin → location → Receive Payments). */
const GATEWAY_UNAVAILABLE_MESSAGE =
  'This payment method is currently unavailable. Please contact your support team or administrator to enable this payment method.';

const INITIAL_FORM: NewSaleFormData = {
  transactionType: 'receive_payment',
  amount: '',
  notes: '',
  customerId: '',
  paymentMethod: 'card',
  accountType: 'business',
  accountCategory: 'checking',
  routingNumber: '',
  accountNumber: '',
  authorizationType: 'prearranged',
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  streetAddress: '',
  city: '',
  state: '',
  zipCode: '',
  idType: 'drivers_license',
  dlState: '',
  dlNumber: '',
  cardNumber: '',
  cardHolderName: '',
  expirationDate: '',
  cvv: '',
  cardZipCode: '',
  cardStreetAddress: '',
  cardAuthorizationType: 'prearranged',
  cardEmail: '',
  authorizationRequired: 'no',
  receiptDelivery: 'email',
};

// ── Section Header ──

function SectionHeader({ title }: { title: string }) {
  return <h3 className="text-sm font-semibold text-foreground border-b border-border pb-2">{title}</h3>;
}

// ── Customer Searchable Dropdown ──

function CustomerDropdown({
  value,
  onChange,
  onAddCustomer,
  onBlur,
  fieldErrors,
}: {
  value: string;
  onChange: (id: string) => void;
  onAddCustomer: () => void;
  onBlur?: () => void;
  fieldErrors: FieldErrors;
}) {
  const { data } = useRPCustomers();
  const customers = (data?.data ?? []).filter((c: RPCustomer) => c.isActive);
  const [search, setSearch] = useState('');
  const [isOpen, setIsOpen] = useState(false);

  const selectedCustomer = customers.find((c: RPCustomer) => c.id === value);

  const filteredCustomers = useMemo(() => {
    if (!search.trim()) return customers;
    const q = search.toLowerCase();
    return customers.filter(
      (c: RPCustomer) =>
        c.name.toLowerCase().includes(q) || c.company?.toLowerCase().includes(q) || c.email?.toLowerCase().includes(q),
    );
  }, [customers, search]);

  return (
    <div className="relative">
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            value={isOpen ? search : selectedCustomer?.name || ''}
            onChange={(e) => {
              setSearch(e.target.value);
              if (!isOpen) setIsOpen(true);
            }}
            onFocus={() => {
              setIsOpen(true);
              setSearch('');
            }}
            onBlur={() => {
              if (!isOpen) onBlur?.();
            }}
            placeholder="Search and select a customer..."
            className={`${fieldErrors.customerId ? inputError : inputNormal} pl-9 ${selectedCustomer ? 'pr-8' : ''}`}
          />
          {selectedCustomer && !isOpen && (
            <button
              type="button"
              onClick={() => {
                onChange('');
                setSearch('');
              }}
              className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground"
            >
              <X className="h-4 w-4" />
            </button>
          )}
          {isOpen && (
            <>
              <div className="fixed inset-0 z-40" onClick={() => setIsOpen(false)} />
              <div className="absolute left-0 right-0 top-11 z-50 max-h-48 overflow-y-auto rounded-lg border border-border bg-white shadow-lg">
                {filteredCustomers.length === 0 ? (
                  <p className="px-3 py-2 text-sm text-muted-foreground">No customers found</p>
                ) : (
                  filteredCustomers.map((c: RPCustomer) => (
                    <button
                      key={c.id}
                      type="button"
                      onClick={() => {
                        onChange(c.id);
                        setIsOpen(false);
                        setSearch('');
                      }}
                      className={cn(
                        'flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-muted transition-colors',
                        value === c.id && 'bg-primary/5 text-primary',
                      )}
                    >
                      <span className="font-medium">{c.name}</span>
                      {c.company && <span className="text-xs text-muted-foreground">({c.company})</span>}
                    </button>
                  ))
                )}
              </div>
            </>
          )}
        </div>
        <button
          type="button"
          onClick={onAddCustomer}
          className="inline-flex items-center gap-1 whitespace-nowrap rounded-lg border border-primary px-3 h-10 text-sm font-medium text-primary hover:bg-primary/5"
        >
          <Plus className="h-4 w-4" />
          Add Customer
        </button>
      </div>
      <FieldError message={fieldErrors.customerId} />
    </div>
  );
}

// ── Main New Sale Tab ──

export function NewSaleTab({ onAddCustomer }: { onAddCustomer: () => void }) {
  const [baseForm, setForm] = useState<NewSaleFormData>({ ...INITIAL_FORM });
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [userEdited, setUserEdited] = useState<Set<string>>(new Set());
  // Statement / Transaction Descriptor: required, user-entered, max 6 chars.
  const [statementSuffix, setStatementSuffix] = useState('');
  const createSale = useCreateSaleTransaction();
  const createRecurring = useCreateRecurringSale();
  const canSchedule = usePermissions().has('rp.sale.schedule');
  // Generate the DL placeholder once at mount and reuse it across submits,
  // so the prefilled value is stable for the session.
  const [placeholderDlNumber] = useState<string>(() => generatePlaceholderDriversLicense());
  // Recurring rule state lives outside the sale form because it is captured
  // alongside the sale but persisted to a separate table on submit.
  const [recurringEnabled, setRecurringEnabled] = useState(false);
  const [recurringFrequency, setRecurringFrequency] = useState<RecurringFrequency>('monthly');
  const [recurringStartDate, setRecurringStartDate] = useState('');
  const [recurringStartTime, setRecurringStartTime] = useState('09:00');
  const [recurringHasEnd, setRecurringHasEnd] = useState(false);
  const [recurringEndDate, setRecurringEndDate] = useState('');
  const [recurringError, setRecurringError] = useState('');
  const [scheduledEnabled, setScheduledEnabled] = useState(false);
  const [scheduledDate, setScheduledDate] = useState('');
  const [scheduledTime, setScheduledTime] = useState('09:00');
  const [scheduledError, setScheduledError] = useState('');
  const { selectedLocationId } = useLocationStore();
  const { data: locationsData } = useLocationsWithExternalId();
  const selectedLocation = locationsData?.data?.find((l: any) => l.id === selectedLocationId);
  const merchantId = selectedLocation?.externalId || null;
  // Card / ACH tabs follow the super admin's payment method switches on this
  // location (under the Receive Payments module gate). Until the location has
  // loaded nothing is disabled, so the form never flashes locked tabs.
  const methodEnabled = useMemo(() => resolveReceivePaymentMethods(selectedLocation), [selectedLocation]);

  // Fetch saved payment methods for selected customer
  const { data: customersData } = useRPCustomers();
  const { data: customerCardsData } = useRPCustomerCards(baseForm.customerId || null);
  const { data: customerBanksData } = useRPCustomerBanks(baseForm.customerId || null);
  // Compute auto-filled form values from saved payment methods (derived state, no side effects)
  // Only auto-fill fields the user has not manually edited
  const form = useMemo<NewSaleFormData>(() => {
    const updates: Partial<NewSaleFormData> = {};

    // Prefill Driver's License placeholders for customers without a real DL
    // when the field becomes mandatory (ACH + prearranged authorization).
    // Both values can be overwritten and are not re-applied after the user edits.
    if (baseForm.paymentMethod === 'ach' && baseForm.authorizationType === 'prearranged') {
      if (!baseForm.dlNumber && !userEdited.has('dlNumber')) {
        updates.dlNumber = placeholderDlNumber;
      }
      if (!baseForm.dlState && !userEdited.has('dlState')) {
        updates.dlState = 'CA';
      }
    }

    if (!baseForm.customerId) {
      return Object.keys(updates).length > 0 ? { ...baseForm, ...updates } : baseForm;
    }

    const cards = customerCardsData?.data ?? [];
    const banks = customerBanksData?.data ?? [];

    // Auto-fill card email from selected customer
    const selectedCustomer = (customersData?.data ?? []).find((c: RPCustomer) => c.id === baseForm.customerId);
    if (selectedCustomer?.email && !userEdited.has('cardEmail')) {
      updates.cardEmail = selectedCustomer.email;
    }

    // Auto-fill ACH account holder fields from customer profile
    if (selectedCustomer) {
      const nameParts = selectedCustomer.name.trim().split(/\s+/);
      if (!userEdited.has('firstName') && !updates.firstName) {
        updates.firstName = nameParts[0] || '';
      }
      if (!userEdited.has('lastName') && !updates.lastName) {
        updates.lastName = nameParts.slice(1).join(' ') || '';
      }
      if (selectedCustomer.email && !userEdited.has('email') && !updates.email) {
        updates.email = selectedCustomer.email;
      }
      if (selectedCustomer.phone && !userEdited.has('phone') && !updates.phone) {
        updates.phone = selectedCustomer.phone;
      }
    }

    // Auto-fill card details if customer has saved cards
    if (cards.length > 0) {
      const card = cards[0];
      if (!userEdited.has('cardNumber'))
        updates.cardNumber = card.cardLastFour ? `•••• •••• •••• ${card.cardLastFour}` : '';
      if (!userEdited.has('cardHolderName')) updates.cardHolderName = card.cardHolderName || '';
      if (!userEdited.has('expirationDate')) updates.expirationDate = card.expirationDate || '';
      if (!userEdited.has('cardZipCode')) updates.cardZipCode = card.cardZipCode || '';
      if (!userEdited.has('cardStreetAddress')) updates.cardStreetAddress = card.streetAddress || '';
    }

    // Auto-fill bank details if customer has saved banks
    if (banks.length > 0) {
      const bank = banks[0];
      if (!userEdited.has('accountType')) updates.accountType = bank.accountCategory || 'business';
      if (!userEdited.has('accountCategory')) updates.accountCategory = bank.accountType || 'checking';
      if (!userEdited.has('routingNumber')) updates.routingNumber = bank.routingNumber || '';
      if (!userEdited.has('accountNumber')) updates.accountNumber = bank.accountNumber || '';
      if (!userEdited.has('firstName')) updates.firstName = bank.firstName || '';
      if (!userEdited.has('lastName')) updates.lastName = bank.lastName || '';
      if (!userEdited.has('email')) updates.email = bank.email || '';
      if (!userEdited.has('phone')) updates.phone = bank.phone || '';
      if (!userEdited.has('streetAddress')) updates.streetAddress = bank.streetAddress || '';
      if (!userEdited.has('city')) updates.city = bank.city || '';
      if (!userEdited.has('state')) updates.state = bank.state || '';
      if (!userEdited.has('zipCode')) updates.zipCode = bank.zipCode || '';
    }

    // Default to card if available, otherwise ACH
    if (!userEdited.has('paymentMethod')) {
      if (cards.length > 0) {
        updates.paymentMethod = 'card';
      } else if (banks.length > 0) {
        updates.paymentMethod = 'ach';
      }
    }

    // A method whose gateway isn't activated can't be selected, even by the
    // saved-method default above — fall over to the other one if it's open.
    const chosen = updates.paymentMethod ?? baseForm.paymentMethod;
    if (!methodEnabled[chosen]) {
      const other = chosen === 'card' ? 'ach' : 'card';
      if (methodEnabled[other]) updates.paymentMethod = other;
    }

    return Object.keys(updates).length > 0 ? { ...baseForm, ...updates } : baseForm;
  }, [baseForm, customersData, customerCardsData, customerBanksData, userEdited, placeholderDlNumber, methodEnabled]);

  const update = (field: keyof NewSaleFormData, value: string) => {
    // When customer changes, clear user-edited tracking so auto-fill applies fresh
    if (field === 'customerId') {
      setUserEdited(new Set());
      // If customer is cleared, reset payment fields to initial values
      if (!value) {
        setForm((prev) => ({
          ...INITIAL_FORM,
          // Preserve non-payment fields
          transactionType: prev.transactionType,
          amount: prev.amount,
          notes: prev.notes,
          customerId: '',
        }));
        return;
      }
    } else {
      setUserEdited((prev) => new Set(prev).add(field));
    }
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const markTouched = (field: string) => setTouched((prev) => ({ ...prev, [field]: true }));

  // Fields belonging to each payment method (used for clearing touched state on switch)
  const achFields = [
    'accountType',
    'accountCategory',
    'routingNumber',
    'accountNumber',
    'authorizationType',
    'firstName',
    'lastName',
    'email',
    'phone',
    'streetAddress',
    'city',
    'state',
    'zipCode',
    'idType',
    'dlState',
    'dlNumber',
  ];
  const cardFields = [
    'cardNumber',
    'cardHolderName',
    'expirationDate',
    'cvv',
    'cardZipCode',
    'cardStreetAddress',
    'cardAuthorizationType',
    'cardEmail',
  ];

  const switchPaymentMethod = (method: 'ach' | 'card') => {
    if (!methodEnabled[method]) {
      toast.warning(GATEWAY_UNAVAILABLE_MESSAGE);
      return;
    }
    if (form.paymentMethod === method) return;
    update('paymentMethod', method);
    // Clear touched state for the OTHER method's fields so errors don't persist
    const fieldsToClear = method === 'ach' ? cardFields : achFields;
    setTouched((prev) => {
      const next = { ...prev };
      fieldsToClear.forEach((f) => delete next[f]);
      return next;
    });
  };

  // ── Validation ──

  const fieldErrors = useMemo<FieldErrors>(() => {
    const errors: FieldErrors = {};

    // Customer (required)
    if (!form.customerId) {
      errors.customerId = 'Please select a customer';
    }

    // Statement / Transaction Descriptor (required, max 6 chars)
    if (!statementSuffix.trim()) {
      errors.statementDescriptor = 'Statement / Transaction Descriptor is required';
    }

    // Amount
    if (!form.amount) {
      errors.amount = 'Amount is required';
    } else {
      const num = parseFloat(form.amount);
      if (isNaN(num) || num < 0.01 || num > 999999.99) {
        errors.amount = 'Total amount must be between $0.01 and $999,999.99';
      }
    }

    // ACH fields (only when ACH selected)
    if (form.paymentMethod === 'ach') {
      if (!form.accountType) errors.accountType = 'Account type is required';
      if (!form.accountCategory) errors.accountCategory = 'Account category is required';

      if (!form.routingNumber) {
        errors.routingNumber = 'Routing number is required';
      } else if (form.routingNumber.length !== 9) {
        errors.routingNumber = 'Routing number must be 9 digits';
      }

      if (!form.accountNumber) {
        errors.accountNumber = 'Account number is required';
      } else if (form.accountNumber.length < 4) {
        errors.accountNumber = 'Account number must be at least 4 digits';
      } else if (form.accountNumber.length > 20) {
        errors.accountNumber = 'Account number must not exceed 20 digits';
      }

      if (!form.authorizationType) errors.authorizationType = 'Authorization type is required';
    }

    // Account holder info (ACH only)
    if (form.paymentMethod === 'ach') {
      const isPhone = form.authorizationType === 'over_the_phone';
      const isPrearranged = form.authorizationType === 'prearranged' && form.authorizationRequired === 'yes';

      // First name & last name: always required
      if (!form.firstName.trim()) {
        errors.firstName = 'First name is required';
      } else if (form.firstName.trim().length < 2) {
        errors.firstName = 'First name must be at least 2 characters';
      }

      if (!form.lastName.trim()) {
        errors.lastName = 'Last name is required';
      } else if (form.lastName.trim().length < 1) {
        errors.lastName = 'Last name must be at least 1 character';
      }

      // Email: optional for Over By Phone, required for Prearranged
      if (isPhone) {
        if (form.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
          errors.email = 'Please enter a valid email address';
        }
      } else if (isPrearranged) {
        if (!form.email.trim()) {
          errors.email = 'Email is required';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
          errors.email = 'Please enter a valid email address';
        }
      }

      // Phone: always required
      {
        const phoneErr = validatePhone(form.phone, true);
        if (phoneErr) errors.phone = phoneErr;
      }

      // Address fields: NOT required for either auth type, but validate format if provided
      if (form.zipCode && form.zipCode.length !== 5) {
        errors.zipCode = 'Zip code must be 5 digits';
      }

      // Account holder ID: required only for Prearranged
      if (isPrearranged) {
        if (!form.dlState) errors.dlState = "Driver's license state is required";

        if (!form.dlNumber.trim()) {
          errors.dlNumber = "Driver's license number is required";
        } else {
          const dlError = validateDriversLicense(form.dlNumber);
          if (dlError) errors.dlNumber = dlError;
        }
      }
    }

    // Card details (Credit / Debit Card only)
    if (form.paymentMethod === 'card') {
      const rawCardNumber = form.cardNumber.replace(/\s/g, '');
      if (!rawCardNumber) {
        errors.cardNumber = 'Card number is required';
      } else if (rawCardNumber.length < 12 || rawCardNumber.length > 19) {
        errors.cardNumber = 'Card number must be between 12 and 19 digits';
      }

      // Card holder name is optional, but if provided must be alphabets+spaces, min 2 chars
      if (form.cardHolderName.trim()) {
        if (form.cardHolderName.trim().length < 2) {
          errors.cardHolderName = 'Card holder name must be at least 2 characters';
        }
      }

      if (!form.expirationDate) {
        errors.expirationDate = 'Expiration date is required';
      } else if (!/^\d{2}\/\d{2}$/.test(form.expirationDate)) {
        errors.expirationDate = 'Expiration date must be in MM/YY format';
      } else {
        const [mm, yy] = form.expirationDate.split('/').map(Number);
        if (mm < 1 || mm > 12) {
          errors.expirationDate = 'Invalid month';
        } else {
          const now = new Date();
          const currentMonth = now.getMonth() + 1;
          const currentYear = now.getFullYear() % 100;
          if (yy < currentYear || (yy === currentYear && mm < currentMonth)) {
            errors.expirationDate = 'Card has expired';
          }
        }
      }

      if (!form.cvv) {
        errors.cvv = 'CVV is required';
      } else if (form.cvv.length < 3 || form.cvv.length > 4) {
        errors.cvv = 'CVV must be 3 or 4 digits';
      }

      if (!form.cardZipCode) {
        errors.cardZipCode = 'Zip code is required';
      } else if (form.cardZipCode.length !== 5) {
        errors.cardZipCode = 'Zip code must be 5 digits';
      }

      // Card street address is optional, but max 127 chars
      if (form.cardStreetAddress.length > 127) {
        errors.cardStreetAddress = 'Street address must not exceed 127 characters';
      }

      if (!form.cardAuthorizationType) {
        errors.cardAuthorizationType = 'Authorization type is required';
      }

      // Email always required for card payments
      if (!form.cardEmail.trim()) {
        errors.cardEmail = 'Customer email is required';
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.cardEmail)) {
        errors.cardEmail = 'Please enter a valid email address';
      }
    }

    return errors;
  }, [form, statementSuffix]);

  const isFormValid = Object.keys(fieldErrors).length === 0;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    // Mark all fields as touched
    const allTouched: Record<string, boolean> = {};
    Object.keys(INITIAL_FORM).forEach((key) => {
      allTouched[key] = true;
    });
    allTouched.statementDescriptor = true;
    setTouched(allTouched);

    if (!isFormValid) return;

    if (!merchantId) {
      toast.error('No location selected or location has no Merchant ID. Please select a location from the header.');
      return;
    }

    // Validate recurring inputs up front so we don't charge the sale and then
    // discover the recurring rule was malformed.
    let recurringPayload: Record<string, unknown> | null = null;
    if (recurringEnabled) {
      if (!recurringStartDate) {
        setRecurringError('Start date is required');
        return;
      }
      const startAtIso = new Date(`${recurringStartDate}T${recurringStartTime || '00:00'}:00`).toISOString();
      if (new Date(startAtIso) < new Date()) {
        setRecurringError('Start date cannot be in the past');
        return;
      }
      let endAtIso: string | null = null;
      if (recurringHasEnd) {
        if (!recurringEndDate) {
          setRecurringError('End date is required');
          return;
        }
        const endDate = new Date(`${recurringEndDate}T23:59:59`);
        if (endDate <= new Date(startAtIso)) {
          setRecurringError('End date must be after the start date');
          return;
        }
        endAtIso = endDate.toISOString();
      }
      setRecurringError('');
      recurringPayload = {
        frequency: recurringFrequency,
        startAt: startAtIso,
        endAt: endAtIso,
      };
    }

    let scheduledPayload: Record<string, unknown> | null = null;
    if (scheduledEnabled) {
      if (!scheduledDate) {
        setScheduledError('Scheduled date is required');
        return;
      }
      const startAtIso = new Date(`${scheduledDate}T${scheduledTime || '00:00'}:00`).toISOString();
      if (new Date(startAtIso) <= new Date()) {
        setScheduledError('Scheduled date must be in the future');
        return;
      }
      setScheduledError('');
      scheduledPayload = {
        frequency: 'once',
        startAt: startAtIso,
      };
    }

    try {
      const salePayload = {
        ...form,
        // For card payments, use cardEmail as the email field the RP service expects
        email: form.paymentMethod === 'card' ? form.cardEmail : form.email,
        statementDescriptor: statementSuffix.trim(),
        locationId: selectedLocationId || undefined,
        merchantId: merchantId || undefined,
      };

      if (recurringPayload) {
        // Recurring flow — create the rule only; the scheduler handles all charges.
        await createRecurring.mutateAsync({ ...salePayload, ...recurringPayload });
        toast.success('Recurring sale scheduled! The first charge will run on the start date.');
      } else if (scheduledPayload) {
        // Schedule for later — one-time future charge via recurring rule with frequency 'once'.
        await createRecurring.mutateAsync({ ...salePayload, ...scheduledPayload });
        toast.success('Payment scheduled! It will be processed on the selected date.');
      } else {
        // One-time sale — completely separate, no recurring side-effects.
        await createSale.mutateAsync(salePayload);
        toast.success('Payment submitted successfully!');
      }

      setForm({ ...INITIAL_FORM });
      setTouched({});
      setUserEdited(new Set());
      setStatementSuffix('');
      setRecurringEnabled(false);
      setRecurringHasEnd(false);
      setRecurringEndDate('');
      setRecurringStartDate('');
      setScheduledEnabled(false);
      setScheduledDate('');
    } catch (err: any) {
      const message = err?.error?.message || 'Failed to submit payment. Please try again.';
      toast.error(message);
    }
  };

  const parsedAmount = parseFloat(form.amount) || 0;

  return (
    <form onSubmit={handleSubmit} className="mx-auto max-w-3xl space-y-6">
      {/* Recurring and scheduled sales need their own permission. */}
      {canSchedule && (
        <>
          {/* 1. Recurring */}
          <div className="rounded-lg border border-border bg-white p-5 space-y-4">
            <SectionHeader title="Recurring" />
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={recurringEnabled}
                onChange={(e) => {
                  const checked = e.target.checked;
                  setRecurringEnabled(checked);
                  setRecurringError('');
                  if (checked) {
                    setScheduledEnabled(false);
                    setScheduledError('');
                    if (!recurringStartDate) {
                      const d = new Date();
                      setRecurringStartDate(
                        `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`,
                      );
                    }
                  }
                }}
                className="mt-0.5 h-4 w-4 accent-primary"
              />
              <span className="text-sm">
                <span className="font-medium text-foreground">Make this a recurring sale</span>
                <span className="block text-xs text-muted-foreground">
                  Schedule this sale to repeat automatically on the cadence you choose.
                </span>
              </span>
            </label>

            {recurringEnabled && (
              <div className="space-y-4 rounded-lg border border-border bg-muted/30 p-4">
                <div>
                  <label className="mb-1 block text-sm font-medium text-foreground">
                    Frequency <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={recurringFrequency}
                    onChange={(e) => setRecurringFrequency(e.target.value as RecurringFrequency)}
                    className={inputNormal}
                  >
                    {RECURRING_FREQUENCIES.map((freq) => (
                      <option key={freq} value={freq}>
                        {RECURRING_FREQUENCY_LABELS[freq]}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <label className="mb-1 block text-sm font-medium text-foreground">
                      Start Date <span className="text-red-500">*</span>
                    </label>
                    <DateInput
                      value={recurringStartDate}
                      min={(() => {
                        const d = new Date();
                        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
                      })()}
                      onChange={(e) => setRecurringStartDate(e.target.value)}
                      className={inputNormal}
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-sm font-medium text-foreground">
                      Start Time <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="time"
                      value={recurringStartTime}
                      onChange={(e) => setRecurringStartTime(e.target.value)}
                      className={inputNormal}
                    />
                  </div>
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-foreground">End</label>
                  <div className="flex items-center gap-6">
                    <label className="flex items-center gap-2 cursor-pointer whitespace-nowrap">
                      <input
                        type="radio"
                        name="recurringEnd"
                        checked={!recurringHasEnd}
                        onChange={() => setRecurringHasEnd(false)}
                        className="h-4 w-4 accent-primary"
                      />
                      <span className="text-sm">No end date</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer whitespace-nowrap">
                      <input
                        type="radio"
                        name="recurringEnd"
                        checked={recurringHasEnd}
                        onChange={() => setRecurringHasEnd(true)}
                        className="h-4 w-4 accent-primary"
                      />
                      <span className="text-sm">End on</span>
                    </label>
                  </div>
                  {recurringHasEnd && (
                    <DateInput
                      value={recurringEndDate}
                      min={
                        recurringStartDate ||
                        (() => {
                          const d = new Date();
                          return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
                        })()
                      }
                      onChange={(e) => setRecurringEndDate(e.target.value)}
                      className={`${inputNormal} mt-2`}
                    />
                  )}
                </div>

                {recurringError && (
                  <p className="rounded border border-red-300 bg-red-50 p-2 text-xs text-red-700" role="alert">
                    {recurringError}
                  </p>
                )}
              </div>
            )}
          </div>

          {/* 2. Schedule for Later */}
          <div className="rounded-lg border border-border bg-white p-5 space-y-4">
            <SectionHeader title="Schedule for Later" />
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={scheduledEnabled}
                onChange={(e) => {
                  const checked = e.target.checked;
                  setScheduledEnabled(checked);
                  setScheduledError('');
                  if (checked) {
                    setRecurringEnabled(false);
                    setRecurringError('');
                  }
                }}
                className="mt-0.5 h-4 w-4 accent-primary"
              />
              <span className="text-sm">
                <span className="font-medium text-foreground">Schedule One time payment for future date</span>
                <span className="block text-xs text-muted-foreground">
                  Process this as a one-time transaction on the date you choose.
                </span>
              </span>
            </label>

            {scheduledEnabled && (
              <div className="space-y-4 rounded-lg border border-border bg-muted/30 p-4">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <label className="mb-1 block text-sm font-medium text-foreground">
                      Scheduled Date <span className="text-red-500">*</span>
                    </label>
                    <DateInput
                      value={scheduledDate}
                      min={(() => {
                        const d = new Date();
                        d.setDate(d.getDate() + 1);
                        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
                      })()}
                      onChange={(e) => setScheduledDate(e.target.value)}
                      className={inputNormal}
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-sm font-medium text-foreground">
                      Scheduled Time <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="time"
                      value={scheduledTime}
                      onChange={(e) => setScheduledTime(e.target.value)}
                      className={inputNormal}
                    />
                  </div>
                </div>
                {scheduledError && (
                  <p className="rounded border border-red-300 bg-red-50 p-2 text-xs text-red-700" role="alert">
                    {scheduledError}
                  </p>
                )}
              </div>
            )}
          </div>
        </>
      )}

      {/* 3. Transaction Details */}
      <div className="rounded-lg border border-border bg-white p-5 space-y-4">
        <SectionHeader title="Transaction Details" />
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <label className="mb-1 block text-sm font-medium text-foreground">
              Transaction Type <span className="text-red-500">*</span>
            </label>
            <select
              value={form.transactionType}
              onChange={(e) => update('transactionType', e.target.value)}
              className={inputNormal}
            >
              <option value="receive_payment">Sale</option>
            </select>
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-foreground">
              Statement / Transaction Descriptor <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={statementSuffix}
              onChange={(e) => setStatementSuffix(e.target.value)}
              onBlur={() => markTouched('statementDescriptor')}
              maxLength={10}
              placeholder="Up to 10 characters"
              className={fc(fieldErrors, 'statementDescriptor')}
              aria-invalid={!!fieldErrors.statementDescriptor}
            />
            {touched.statementDescriptor && <FieldError message={fieldErrors.statementDescriptor} />}
          </div>
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <label className="mb-1 block text-sm font-medium text-foreground">
              Amount <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">$</span>
              <input
                type="text"
                value={form.amount}
                onChange={(e) => {
                  const value = e.target.value.replace(/[^0-9.]/g, '');
                  const parts = value.split('.');
                  let sanitized = parts.length > 2 ? parts[0] + '.' + parts.slice(1).join('') : value;
                  if (parts[1]?.length > 2) {
                    sanitized = parts[0] + '.' + parts[1].slice(0, 2);
                  }
                  update('amount', sanitized);
                }}
                onBlur={() => markTouched('amount')}
                placeholder="0.00"
                inputMode="decimal"
                className={`${fc(fieldErrors, 'amount')} pl-7`}
                aria-invalid={!!fieldErrors.amount}
              />
            </div>
            {touched.amount && <FieldError message={fieldErrors.amount} />}
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-foreground">Notes</label>
            {/* Capped at 40. maxLength stops typing; the slice covers the paths
                that skip it (paste on some mobile keyboards, IME composition) —
                same belt-and-braces as Street Address below. What the field is
                FOR moved out of the placeholder into help text, so it stays
                readable once there is a value in the box. */}
            <input
              type="text"
              value={form.notes}
              onChange={(e) => update('notes', e.target.value.slice(0, 40))}
              placeholder="Up to 40 characters"
              maxLength={40}
              className={fc(fieldErrors, 'notes')}
              aria-invalid={!!fieldErrors.notes}
            />
            {/* Help text and counter share a row so the count sits at the right
                edge of the input, as it does on Street Address. `shrink-0` keeps
                it intact when the help text wraps on a narrow screen. */}
            <div className="flex justify-between gap-3">
              <p className="mt-1 text-xs text-muted-foreground">
                References such as Notes, Invoice No, Bill No, Purpose etc...
              </p>
              <span className="mt-1 shrink-0 text-xs text-muted-foreground">{form.notes.length}/40</span>
            </div>
            {touched.notes && <FieldError message={fieldErrors.notes} />}
          </div>
        </div>
      </div>

      {/* 3. Payment Information */}
      <div className="rounded-lg border border-border bg-white p-5 space-y-4">
        <SectionHeader title="Payment Information" />

        {/* Customer */}
        <div>
          <label className="mb-1 block text-sm font-medium text-foreground">
            Customer <span className="text-red-500">*</span>
          </label>
          <CustomerDropdown
            value={form.customerId}
            onChange={(id) => update('customerId', id)}
            onAddCustomer={onAddCustomer}
            fieldErrors={touched.customerId ? fieldErrors : {}}
            onBlur={() => markTouched('customerId')}
          />
        </div>

        {/* Payment Method Toggle */}
        <div>
          <label className="mb-2 block text-sm font-medium text-foreground">
            Payment Method <span className="text-red-500">*</span>
          </label>
          <div className="grid grid-cols-2 gap-3">
            {/* Unavailable tabs stay clickable (aria-disabled, not disabled) so the
                hover tooltip and the explanatory toast on click both still fire. */}
            <button
              type="button"
              onClick={() => switchPaymentMethod('card')}
              aria-disabled={!methodEnabled.card}
              title={methodEnabled.card ? undefined : GATEWAY_UNAVAILABLE_MESSAGE}
              className={cn(
                'flex items-center justify-center gap-2 rounded-lg p-3 text-sm font-medium transition-all',
                form.paymentMethod === 'card'
                  ? 'border-2 border-primary bg-primary text-primary-foreground shadow-sm'
                  : 'border-2 border-border text-muted-foreground hover:border-primary/40',
                !methodEnabled.card && 'cursor-not-allowed opacity-50 hover:border-border',
              )}
            >
              <CreditCard className="h-5 w-5" />
              Credit / Debit Card
            </button>
            <button
              type="button"
              onClick={() => switchPaymentMethod('ach')}
              aria-disabled={!methodEnabled.ach}
              title={methodEnabled.ach ? undefined : GATEWAY_UNAVAILABLE_MESSAGE}
              className={cn(
                'flex items-center justify-center gap-2 rounded-lg p-3 text-sm font-medium transition-all',
                form.paymentMethod === 'ach'
                  ? 'border-2 border-primary bg-primary text-primary-foreground shadow-sm'
                  : 'border-2 border-border text-muted-foreground hover:border-primary/40',
                !methodEnabled.ach && 'cursor-not-allowed opacity-50 hover:border-border',
              )}
            >
              <Landmark className="h-5 w-5" />
              Bank Account (ACH)
            </button>
          </div>
        </div>
      </div>

      {/* 4. Account Information (ACH) */}
      {form.paymentMethod === 'ach' && (
        <div className="rounded-lg border border-border bg-white p-5 space-y-4">
          <SectionHeader title="Account Information" />

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className="mb-1 block text-sm font-medium text-foreground">
                Account <span className="text-red-500">*</span>
              </label>
              <select
                value={form.accountType}
                onChange={(e) => update('accountType', e.target.value)}
                onBlur={() => markTouched('accountType')}
                className={fc(fieldErrors, 'accountType')}
              >
                <option value="">Select</option>
                <option value="business">Business</option>
                <option value="personal">Personal</option>
              </select>
              {touched.accountType && <FieldError message={fieldErrors.accountType} />}
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-foreground">
                Account Type <span className="text-red-500">*</span>
              </label>
              <select
                value={form.accountCategory}
                onChange={(e) => update('accountCategory', e.target.value)}
                onBlur={() => markTouched('accountCategory')}
                className={fc(fieldErrors, 'accountCategory')}
              >
                <option value="">Select</option>
                <option value="checking">Checking</option>
                <option value="savings">Savings</option>
              </select>
              {touched.accountCategory && <FieldError message={fieldErrors.accountCategory} />}
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className="mb-1 block text-sm font-medium text-foreground">
                Routing Number <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={form.routingNumber}
                onChange={(e) => update('routingNumber', e.target.value.replace(/\D/g, '').slice(0, 9))}
                onBlur={() => markTouched('routingNumber')}
                placeholder="123456789"
                maxLength={9}
                inputMode="numeric"
                className={fc(fieldErrors, 'routingNumber')}
                aria-invalid={!!fieldErrors.routingNumber}
              />
              {touched.routingNumber && <FieldError message={fieldErrors.routingNumber} />}
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-foreground">
                Account Number <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={form.accountNumber}
                onChange={(e) => update('accountNumber', e.target.value.replace(/\D/g, '').slice(0, 20))}
                onBlur={() => markTouched('accountNumber')}
                placeholder="000123456789"
                maxLength={20}
                inputMode="numeric"
                className={fc(fieldErrors, 'accountNumber')}
                aria-invalid={!!fieldErrors.accountNumber}
              />
              {touched.accountNumber && <FieldError message={fieldErrors.accountNumber} />}
            </div>
          </div>
        </div>
      )}

      {/* 5. Account Holder Information (ACH only) */}
      {form.paymentMethod === 'ach' && (
        <div className="rounded-lg border border-border bg-white p-5 space-y-4">
          <SectionHeader title="Account Holder Information" />

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className="mb-1 block text-sm font-medium text-foreground">
                First Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={form.firstName}
                onChange={(e) => update('firstName', e.target.value.replace(/[^A-Za-z\s]/g, ''))}
                onBlur={() => markTouched('firstName')}
                placeholder="John"
                className={fc(fieldErrors, 'firstName')}
                aria-invalid={!!fieldErrors.firstName}
              />
              {touched.firstName && <FieldError message={fieldErrors.firstName} />}
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-foreground">
                Last Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={form.lastName}
                onChange={(e) => update('lastName', e.target.value.replace(/[^A-Za-z\s]/g, ''))}
                onBlur={() => markTouched('lastName')}
                placeholder="Doe"
                className={fc(fieldErrors, 'lastName')}
                aria-invalid={!!fieldErrors.lastName}
              />
              {touched.lastName && <FieldError message={fieldErrors.lastName} />}
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className="mb-1 block text-sm font-medium text-foreground">
                Email {form.authorizationType === 'prearranged' && <span className="text-red-500">*</span>}
              </label>
              <input
                type="email"
                value={form.email}
                onChange={(e) => update('email', e.target.value)}
                onBlur={() => markTouched('email')}
                placeholder="john@example.com"
                className={fc(fieldErrors, 'email')}
                aria-invalid={!!fieldErrors.email}
              />
              {touched.email && <FieldError message={fieldErrors.email} />}
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-foreground">
                Phone Number <span className="text-red-500">*</span>
              </label>
              <PhoneInput
                value={form.phone}
                onValueChange={(digits) => update('phone', digits)}
                onBlur={() => markTouched('phone')}
                placeholder="123-456-7890"
                className={fc(fieldErrors, 'phone')}
                aria-invalid={!!fieldErrors.phone}
              />
              {touched.phone && <FieldError message={fieldErrors.phone} />}
            </div>
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-foreground">Street Address</label>
            <input
              type="text"
              value={form.streetAddress}
              onChange={(e) => update('streetAddress', e.target.value)}
              onBlur={() => markTouched('streetAddress')}
              placeholder="123 Main St, Suite 100"
              className={fc(fieldErrors, 'streetAddress')}
              aria-invalid={!!fieldErrors.streetAddress}
            />
            {touched.streetAddress && <FieldError message={fieldErrors.streetAddress} />}
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div>
              <label className="mb-1 block text-sm font-medium text-foreground">City</label>
              <input
                type="text"
                value={form.city}
                onChange={(e) => update('city', e.target.value.replace(/[^A-Za-z\s]/g, ''))}
                onBlur={() => markTouched('city')}
                placeholder="San Francisco"
                className={fc(fieldErrors, 'city')}
                aria-invalid={!!fieldErrors.city}
              />
              {touched.city && <FieldError message={fieldErrors.city} />}
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-foreground">State</label>
              <select
                value={form.state}
                onChange={(e) => update('state', e.target.value)}
                onBlur={() => markTouched('state')}
                className={fc(fieldErrors, 'state')}
              >
                <option value="">Select State</option>
                {US_STATES.map((s) => (
                  <option key={s.value} value={s.value}>
                    {s.label}
                  </option>
                ))}
              </select>
              {touched.state && <FieldError message={fieldErrors.state} />}
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-foreground">Zip Code</label>
              <input
                type="text"
                value={form.zipCode}
                onChange={(e) => update('zipCode', e.target.value.replace(/\D/g, '').slice(0, 5))}
                onBlur={() => markTouched('zipCode')}
                placeholder="94102"
                maxLength={5}
                inputMode="numeric"
                className={fc(fieldErrors, 'zipCode')}
                aria-invalid={!!fieldErrors.zipCode}
              />
              {touched.zipCode && <FieldError message={fieldErrors.zipCode} />}
            </div>
          </div>
        </div>
      )}

      {/* 6. Account Holder ID (ACH only) */}
      {form.paymentMethod === 'ach' && (
        <div className="rounded-lg border border-border bg-white p-5 space-y-4">
          <SectionHeader title="Account Holder ID" />

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div>
              <label className="mb-1 block text-sm font-medium text-foreground">ID Type</label>
              <select value={form.idType} onChange={(e) => update('idType', e.target.value)} className={inputNormal}>
                <option value="drivers_license">Driver&apos;s License</option>
              </select>
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-foreground">
                Driver&apos;s License State{' '}
                {form.authorizationType === 'prearranged' && <span className="text-red-500">*</span>}
              </label>
              <select
                value={form.dlState}
                onChange={(e) => update('dlState', e.target.value)}
                onBlur={() => markTouched('dlState')}
                className={fc(fieldErrors, 'dlState')}
              >
                <option value="">Select State</option>
                {US_STATES.map((s) => (
                  <option key={s.value} value={s.value}>
                    {s.label}
                  </option>
                ))}
              </select>
              {touched.dlState && <FieldError message={fieldErrors.dlState} />}
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-foreground">
                Driver&apos;s License Number{' '}
                {form.authorizationType === 'prearranged' && <span className="text-red-500">*</span>}
              </label>
              <input
                type="text"
                value={form.dlNumber}
                onChange={(e) => update('dlNumber', sanitizeDriversLicense(e.target.value))}
                onBlur={() => markTouched('dlNumber')}
                placeholder="D1234567"
                className={fc(fieldErrors, 'dlNumber')}
                aria-invalid={!!fieldErrors.dlNumber}
              />
              {touched.dlNumber && <FieldError message={fieldErrors.dlNumber} />}
            </div>
          </div>
        </div>
      )}

      {/* Card Details (Credit / Debit Card only) */}
      {form.paymentMethod === 'card' && (
        <div className="rounded-lg border border-border bg-white p-5 space-y-4">
          <SectionHeader title="Card Details" />

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {/* Card Number */}
            <div>
              <label className="mb-1 block text-sm font-medium text-foreground">
                Card Number <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <CreditCard className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <input
                  type="text"
                  value={form.cardNumber}
                  onChange={(e) => {
                    const digits = e.target.value.replace(/\D/g, '').slice(0, 19);
                    const formatted = digits.replace(/(.{4})/g, '$1 ').trim();
                    update('cardNumber', formatted);
                  }}
                  onBlur={() => markTouched('cardNumber')}
                  placeholder="1234 5678 9012 3456"
                  maxLength={23}
                  inputMode="numeric"
                  className={`${fc(fieldErrors, 'cardNumber')} pl-9`}
                  aria-invalid={!!fieldErrors.cardNumber}
                />
              </div>
              {touched.cardNumber && <FieldError message={fieldErrors.cardNumber} />}
            </div>

            {/* Card Holder Name */}
            <div>
              <label className="mb-1 block text-sm font-medium text-foreground">Card Holder Name</label>
              <input
                type="text"
                value={form.cardHolderName}
                onChange={(e) => update('cardHolderName', e.target.value.replace(/[^A-Za-z\s]/g, ''))}
                onBlur={() => markTouched('cardHolderName')}
                placeholder="John Doe"
                className={fc(fieldErrors, 'cardHolderName')}
                aria-invalid={!!fieldErrors.cardHolderName}
              />
              {touched.cardHolderName && <FieldError message={fieldErrors.cardHolderName} />}
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            {/* Expiration Date */}
            <div>
              <label className="mb-1 block text-sm font-medium text-foreground">
                Expiration Date <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={form.expirationDate}
                onChange={(e) => {
                  let value = e.target.value.replace(/[^0-9/]/g, '');
                  // Remove extra slashes
                  const slashCount = (value.match(/\//g) || []).length;
                  if (slashCount > 1) value = value.replace(/\/([^]*)$/, '$1');
                  // Auto-insert slash after MM
                  const digits = value.replace(/\//g, '');
                  if (digits.length >= 2 && !value.includes('/')) {
                    value = digits.slice(0, 2) + '/' + digits.slice(2);
                  }
                  // Limit to MM/YY (5 chars)
                  value = value.slice(0, 5);
                  update('expirationDate', value);
                }}
                onBlur={() => markTouched('expirationDate')}
                placeholder="MM/YY"
                maxLength={5}
                inputMode="numeric"
                className={fc(fieldErrors, 'expirationDate')}
                aria-invalid={!!fieldErrors.expirationDate}
              />
              {touched.expirationDate && <FieldError message={fieldErrors.expirationDate} />}
            </div>

            {/* CVV */}
            <div>
              <label className="mb-1 block text-sm font-medium text-foreground">
                CVV <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={form.cvv}
                onChange={(e) => update('cvv', e.target.value.replace(/\D/g, '').slice(0, 4))}
                onBlur={() => markTouched('cvv')}
                placeholder="123"
                maxLength={4}
                inputMode="numeric"
                className={fc(fieldErrors, 'cvv')}
                aria-invalid={!!fieldErrors.cvv}
              />
              {touched.cvv && <FieldError message={fieldErrors.cvv} />}
            </div>

            {/* Zip Code */}
            <div>
              <label className="mb-1 block text-sm font-medium text-foreground">
                Zip Code <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={form.cardZipCode}
                onChange={(e) => update('cardZipCode', e.target.value.replace(/\D/g, '').slice(0, 5))}
                onBlur={() => markTouched('cardZipCode')}
                placeholder="94102"
                maxLength={5}
                inputMode="numeric"
                className={fc(fieldErrors, 'cardZipCode')}
                aria-invalid={!!fieldErrors.cardZipCode}
              />
              {touched.cardZipCode && <FieldError message={fieldErrors.cardZipCode} />}
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {/* Street Address */}
            <div>
              <label className="mb-1 block text-sm font-medium text-foreground">Street Address</label>
              <input
                type="text"
                value={form.cardStreetAddress}
                onChange={(e) => update('cardStreetAddress', e.target.value.slice(0, 127))}
                onBlur={() => markTouched('cardStreetAddress')}
                placeholder="123 Main St, Suite 100"
                maxLength={127}
                className={fc(fieldErrors, 'cardStreetAddress')}
                aria-invalid={!!fieldErrors.cardStreetAddress}
              />
              <div className="flex justify-between">
                <div>{touched.cardStreetAddress && <FieldError message={fieldErrors.cardStreetAddress} />}</div>
                <span className="mt-1 text-xs text-muted-foreground">{form.cardStreetAddress.length}/127</span>
              </div>
            </div>

            {/* Customer Email */}
            <div>
              <label className="mb-1 block text-sm font-medium text-foreground">
                Customer Email <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                value={form.cardEmail}
                onChange={(e) => update('cardEmail', e.target.value)}
                onBlur={() => markTouched('cardEmail')}
                placeholder="customer@example.com"
                className={fc(fieldErrors, 'cardEmail')}
                aria-invalid={!!fieldErrors.cardEmail}
              />
              {touched.cardEmail && <FieldError message={fieldErrors.cardEmail} />}
            </div>
          </div>
        </div>
      )}

      {/* 8. Authorization */}
      <div className="rounded-lg border border-border bg-white p-5 space-y-4">
        <SectionHeader title="Customer Authorization" />
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <label className="mb-2 block text-sm font-medium text-foreground">
              How is this transaction being authorized? <span className="text-red-500">*</span>
            </label>
            <div className="flex items-center gap-6">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="authorizationTypeUnified"
                  value="prearranged"
                  checked={
                    (form.paymentMethod === 'ach' ? form.authorizationType : form.cardAuthorizationType) ===
                    'prearranged'
                  }
                  onChange={(e) =>
                    update(form.paymentMethod === 'ach' ? 'authorizationType' : 'cardAuthorizationType', e.target.value)
                  }
                  className="h-4 w-4 accent-primary"
                />
                <FileCheck className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm">Prearranged</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="authorizationTypeUnified"
                  value="over_the_phone"
                  checked={
                    (form.paymentMethod === 'ach' ? form.authorizationType : form.cardAuthorizationType) ===
                    'over_the_phone'
                  }
                  onChange={(e) =>
                    update(form.paymentMethod === 'ach' ? 'authorizationType' : 'cardAuthorizationType', e.target.value)
                  }
                  className="h-4 w-4 accent-primary"
                />
                <Phone className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm">Over the Phone</span>
              </label>
            </div>
          </div>

          {(form.paymentMethod === 'ach' ? form.authorizationType : form.cardAuthorizationType) === 'prearranged' && (
            <div>
              <label className="mb-2 block text-sm font-medium text-foreground">
                Is authorization required? <span className="text-red-500">*</span>
              </label>
              <div className="flex items-center gap-6">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="authorizationRequired"
                    value="yes"
                    checked={form.authorizationRequired === 'yes'}
                    onChange={(e) => update('authorizationRequired', e.target.value)}
                    className="h-4 w-4 accent-primary"
                  />
                  <span className="text-sm">Yes</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="authorizationRequired"
                    value="no"
                    checked={form.authorizationRequired === 'no'}
                    onChange={(e) => update('authorizationRequired', e.target.value)}
                    className="h-4 w-4 accent-primary"
                  />
                  <span className="text-sm">No</span>
                </label>
              </div>
            </div>
          )}
        </div>

        {(form.paymentMethod === 'ach' ? form.authorizationType : form.cardAuthorizationType) === 'over_the_phone' && (
          <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 space-y-3 text-sm text-blue-900">
            <p className="font-semibold">
              Authorization Requirements for TEL (Payments initiated via a Merchant facing UI when Authorization is
              received orally via phone.)
            </p>
            <p>
              Must be recorded or send customer written receipt of transaction. Cannot be used in cold calling
              situations – their must be an existing relationship or customer calls merchant.
            </p>
            <p className="font-medium">The following TEL Script will need to be used by merchant for TEL payments:</p>
            <div className="space-y-2 italic">
              <p>
                I want to confirm that your verbal authorization given on (today's date), will be used to initiate a
                one-time ACH debit entry to your bank account number (truncated bank account #), routing number (routing
                #).
              </p>
              <p>
                Per this authorization, (company name) will be charging you (customer's full name) (amount) on (payment
                date or payment schedule for recurring) for (brief description of what the customer is paying for).
              </p>
              <p>
                You will be receiving an e-mail from us at the following email address (email address) that summarizes
                all this information and confirms your payment. If you do not receive the e-mail, or if you have any
                questions about this payment, or wish to revoke your authorization, you can contact us at (phone #)
                during our regular business hours of (business hours).
              </p>
              <p>If this information is correct, please say "I agree" to authorize this transaction.</p>
            </div>
          </div>
        )}
      </div>

      {/* 9. Receipt Delivery */}
      <div className="rounded-lg border border-border bg-white p-5 space-y-4">
        <SectionHeader title="Receipt Delivery" />
        <div className="flex items-center gap-6">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name="receiptDelivery"
              value="email"
              checked={form.receiptDelivery === 'email'}
              onChange={(e) => update('receiptDelivery', e.target.value)}
              className="h-4 w-4 accent-primary"
            />
            <span className="text-sm">Email</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name="receiptDelivery"
              value="none"
              checked={form.receiptDelivery === 'none'}
              onChange={(e) => update('receiptDelivery', e.target.value)}
              className="h-4 w-4 accent-primary"
            />
            <span className="text-sm">None</span>
          </label>
        </div>
      </div>

      {/* 10. Total Amount + Submit */}
      <div className="rounded-lg border border-border bg-white p-5 space-y-4">
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium text-muted-foreground">Total Amount</span>
          <span className="text-2xl font-bold text-primary">{formatCurrency(parsedAmount)}</span>
        </div>

        <button
          type="submit"
          disabled={!isFormValid || createSale.isPending || createRecurring.isPending}
          className="w-full h-11 rounded-lg bg-primary text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {createSale.isPending || createRecurring.isPending
            ? 'Processing...'
            : scheduledEnabled
              ? 'Schedule Payment'
              : recurringEnabled
                ? 'Schedule Recurring'
                : 'Submit Payment'}
        </button>
      </div>
    </form>
  );
}
